<template>
  <div>
    <SearchBarSmallScreenIs24 v-if="isIs24" />
    <div
      data-testid="view-wrapper"
      :class="{
        [$style.contentHg]: !isIs24,
        [$style.contentIs24]: isIs24,
        [$style.dataView]: viewMode === ViewMode.Data,
        [$style.mapView]: viewMode === ViewMode.Map,
      }"
    >
      <DataViewLayer />
      <MapWrapper :class="{ [$style.tabletView]: isTabletScreen }" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { useHead, type ResolvableLink } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onServerPrefetch, provide, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { MapWrapper } from '@/components/map';
import DataViewLayer from '@/components/map/DataViewLayer.vue';
import { SearchBarSmallScreenIs24 } from '@/components/search-bar';
import useAfterRouterLocationChange from '@/composables/useAfterRouterLocationChange';
import { getCulture, localizeLanguage } from '@/helpers/localizeLanguage';
import { isPlatformIs24 } from '@/helpers/platform';
import { getBasePathWithLocation } from '@/router';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { trackHomePageView } from '@/tracking';
import { ViewMode } from '@/types';

import { useMedia, type MediaContext } from '../composables/useMedia';

const { t, locale } = useI18n();
const route = useRoute();

const isIs24 = isPlatformIs24();
const geoStore = useGeoStore();
const mapStore = useMapStore();
const { propertyType, viewMode } = storeToRefs(mapStore);
const { getUrlByGeoLocation, getLocationSentence } = storeToRefs(geoStore);
const { updateMapAndData } = useAfterRouterLocationChange();

const { isSmallScreen, isLargeScreen, isTabletScreen } = useMedia();
provide<MediaContext>('mediaScreen', {
  isSmallScreen,
  isLargeScreen,
  isTabletScreen,
});
const locationParams = computed<string>(() => route.params.location as string);
const locationQuery = computed<string>(() => route.query.zipCity as string);

watch(
  [locationParams, locationQuery],
  async () => {
    await updateMapAndData();
    mapStore.setLoading(false);
  },
  { deep: true },
);
watch(locale, () => {
  fetchPriceDistributions();
});

onMounted(async () => {
  trackHomePageView(locale.value as HgSupportedLanguage);
});

useHead({
  title: () =>
    t('metaKeys.title', [
      new Date().getFullYear(),
      getLocationSentence.value(t, locale.value as HgSupportedLanguage),
    ]),
  meta: [
    {
      name: 'description',
      content: () =>
        t('metaKeys.description', [
          getLocationSentence.value(t, locale.value as HgSupportedLanguage),
        ]) as string,
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: () => import.meta.env.VUE_PLATFORM_BASE_URL + route.path,
    } satisfies ResolvableLink,
    ...Object.values(HgSupportedLanguage).map(
      (language) =>
        ({
          rel: 'alternate',
          hreflang: localizeLanguage(language),
          href: () =>
            `${import.meta.env.VUE_PLATFORM_BASE_URL}${getBasePathWithLocation(language, getUrlByGeoLocation.value[language].param)}`,
        }) satisfies ResolvableLink,
    ),
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${import.meta.env.VUE_PLATFORM_BASE_URL}${getBasePathWithLocation(HgSupportedLanguage.DE, getUrlByGeoLocation.value[HgSupportedLanguage.DE].param)}`,
    } satisfies ResolvableLink,
  ],
});

onServerPrefetch(async () => {
  await Promise.all([fetchPriceDistributions(), updateMapAndData(true, false)]);
});

const fetchPriceDistributions = async () => {
  await mapStore.fetchPriceDistributions(
    propertyType.value,
    getCulture(locale.value as HgSupportedLanguage),
  );
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

$side-bar-width: 440px;

.heading {
  margin: 0 0 1rem;

  @include heading-1-regular;
}

.contentHg,
.contentIs24 {
  > div:first-child {
    @include grid-media-query(sm) {
      flex: 0 0 $side-bar-width;
      overflow: auto;
    }
  }
}

.contentHg {
  flex-direction: row-reverse;

  @include grid-media-query(sm) {
    display: flex;
    height: calc(100vh - #{$header-height-lg-hg});
  }
}

.contentIs24 {
  &.mapView {
    > div:first-child {
      display: none;

      @include grid-media-query(sm) {
        display: block;
      }
    }
  }

  &.dataView {
    > div:last-child {
      display: none;

      @include grid-media-query(sm) {
        display: block;
      }
    }
  }

  @include grid-media-query(sm) {
    display: flex;
    height: calc(100vh - #{$header-height-lg-is24});
  }
}

.tabletView {
  flex: 1;
}
</style>
